const formatPayrollMoney = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('ko-KR').format(Math.round(num || 0));
};

/** 시스템 관리자 또는 센터접속(관리자 권한으로 실행 중) */
export const shouldShowAdminDeductionTooltip = (
  isAdmin: boolean,
  isImpersonating: boolean
): boolean => {
  return isAdmin || isImpersonating;
};

export const buildDeductionTooltip = (
  field: string,
  payroll: any,
  settings: any
): string => {
  if (!payroll) return '';

  const fmt = formatPayrollMoney;
  const ed = payroll.employeeDeductions || {};
  const calcInfo = payroll.calcInfo || {};
  const taxableAmount = payroll.taxableAmount || 0;

  switch (field) {
    case 'nationalPension':
      if (!settings?.applyNationalPension) return '미적용';
      return `국민연금 납부액(직접입력) = ${fmt(ed.nationalPension || 0)}원`;
    case 'healthInsurance':
      if (!settings?.applyHealthInsurance) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × 3.545% = ${fmt(ed.healthInsurance || 0)}원`;
    case 'longTermCare':
      if (!settings?.applyHealthInsurance) return '미적용';
      return `건강보험(${fmt(ed.healthInsurance || 0)}원) × 12.95% = ${fmt(ed.longTermCare || 0)}원`;
    case 'employmentInsurance':
      if (!settings?.applyEmploymentInsurance) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × 0.9% = ${fmt(ed.employmentInsurance || 0)}원`;
    case 'incomeTax':
      return `간이세액표(과세금액 ${fmt(taxableAmount)}원) = ${fmt(ed.incomeTax || 0)}원`;
    case 'localTax':
      return `소득세(${fmt(ed.incomeTax || 0)}원) × 10% = ${fmt(ed.localTax || 0)}원`;
    case 'boninPay':
      return `가족요양 본인부담금 공제 = ${fmt(payroll.boninPayAmount || 0)}원`;
    case 'empEtc':
      return `기타 공제 = ${fmt(payroll.empEtcAmount || 0)}원`;
    case 'supportDeduction': {
      const sd = calcInfo.supportDeductionDetails;
      if (!sd || sd.base === 0) {
        return '지원공제 대상 없음\n\n장기근속수당 또는 중증수급자가산이 있을 때 적용됩니다.';
      }

      const pct = (rate: number) => (rate * 100).toFixed(2);
      let tooltip = '[지원공제 계산]\n\n';

      tooltip += `▶ 기준금액\n`;
      if (sd.longServicePay > 0) {
        tooltip += `   • 장기근속수당: ${fmt(sd.longServicePay)}원\n`;
      }
      if (sd.severeCareAddition > 0) {
        tooltip += `   • 중증수급자가산: ${fmt(sd.severeCareAddition)}원\n`;
      }
      tooltip += `   • 합계: ${fmt(sd.base)}원\n\n`;

      tooltip += `▶ 세부 공제\n`;
      if (sd.nationalPension > 0) {
        tooltip += `   • 국민연금: ${fmt(sd.base)}원 × ${pct(sd.rates.nationalPension)}% = ${fmt(sd.nationalPension)}원\n`;
      }
      if (sd.healthInsurance > 0) {
        tooltip += `   • 건강보험: ${fmt(sd.base)}원 × ${pct(sd.rates.healthInsurance)}% = ${fmt(sd.healthInsurance)}원\n`;
      }
      if (sd.longTermCare > 0) {
        tooltip += `   • 장기요양: ${fmt(sd.healthInsurance)}원 × ${pct(sd.rates.longTermCare)}% = ${fmt(sd.longTermCare)}원\n`;
      }
      if (sd.employmentInsurance > 0) {
        tooltip += `   • 고용보험: ${fmt(sd.base)}원 × ${pct(sd.rates.employmentInsurance)}% = ${fmt(sd.employmentInsurance)}원\n`;
      }
      if (sd.unemployment > 0) {
        tooltip += `   • 실업급여: ${fmt(sd.base)}원 × ${pct(sd.rates.unemployment)}% = ${fmt(sd.unemployment)}원\n`;
      }
      if (sd.industrialAccident > 0) {
        tooltip += `   • 산재보험: ${fmt(sd.base)}원 × ${pct(sd.rates.industrialAccident)}% = ${fmt(sd.industrialAccident)}원\n`;
      }
      if (sd.retirement > 0) {
        tooltip += `   • 퇴직금: ${fmt(sd.base)}원 ÷ 12 = ${fmt(sd.retirement)}원\n`;
      }

      tooltip += `\n지원공제 합계: ${fmt(payroll.supportDeduction || 0)}원`;
      return tooltip;
    }
    case 'totalDeduction':
      return `4대보험 + 세금 + 본인부담금 + 기타공제 + 지원공제 = ${fmt(payroll.totalEmployeeDeduction || 0)}원`;
    default:
      return '';
  }
};

export const buildCenterTooltip = (
  field: string,
  payroll: any,
  settings: any
): string => {
  if (!payroll) return '';

  const fmt = formatPayrollMoney;
  const cd = payroll.centerDeductions || {};
  const taxableAmount = payroll.taxableAmount || 0;
  const totalPay = payroll.totalPay || 0;

  switch (field) {
    case 'insurance4': {
      const np = cd.nationalPension || 0;
      const hi = cd.healthInsurance || 0;
      const ltc = cd.longTermCare || 0;
      const ei = cd.employmentInsurance || 0;
      return `[4대보험 기관부담]\n` +
        `• 국민연금: ${fmt(np)}원 (근로자와 동일)\n` +
        `• 건강보험: 과세금액(${fmt(taxableAmount)}원) × 3.545% = ${fmt(hi)}원\n` +
        `• 장기요양: 건강보험(${fmt(hi)}원) × 12.95% = ${fmt(ltc)}원\n` +
        `• 고용보험: 과세금액(${fmt(taxableAmount)}원) × 0.9% = ${fmt(ei)}원\n` +
        `합계: ${fmt(np + hi + ltc + ei)}원`;
    }
    case 'unemployment':
      if (!settings?.applyUnemployment) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × 0.25% = ${fmt(cd.unemployment || 0)}원`;
    case 'industrialAccident':
      return `과세금액(${fmt(taxableAmount)}원) × 1.78% = ${fmt(cd.industrialAccident || 0)}원`;
    case 'retirement':
      if (!settings?.applyRetirement) return '미적용';
      return `총급여(${fmt(totalPay)}원) ÷ 12 = ${fmt(cd.retirement || 0)}원`;
    case 'totalCenter':
      return `4대보험 + 실업급여 + 산재보험 + 퇴직금 = ${fmt(payroll.totalCenterDeduction || 0)}원`;
    case 'nationalPension':
      if (!settings?.applyNationalPension) return '미적용';
      return `국민연금 기관부담(근로자와 동일) = ${fmt(cd.nationalPension || 0)}원`;
    case 'healthInsurance':
      if (!settings?.applyHealthInsurance) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × 3.545% = ${fmt(cd.healthInsurance || 0)}원`;
    case 'longTermCare':
      if (!settings?.applyHealthInsurance) return '미적용';
      return `건강보험(${fmt(cd.healthInsurance || 0)}원) × 12.95% = ${fmt(cd.longTermCare || 0)}원`;
    case 'employmentInsurance':
      if (!settings?.applyEmploymentInsurance) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × 0.9% = ${fmt(cd.employmentInsurance || 0)}원`;
    default:
      return '';
  }
};
